import Vue from "vue";
import axios from "@/plugins/axios";
import { NARRATIVE_MAX, NOT_REACHABLE } from "@/services/scanReportMessages";
import { adaptScanSummary, scanDocumentPath } from "@/services/comparisonAdapter";

/*
  Carrier security scan summary of a submission (S2BWB-344, part 4).

  Contract (broker auth), under /brokers/customers/:org/submissions/:id/scan-summary:
    - GET    ""                         the summary payload  -> { success, summary }
    - POST   /regenerate                re-read failed reports and rebuild
    - PUT    /narrative                 { text } the broker's own summary
    - POST   /narrative/accept-newer    drop the edit, show the generated summary
    - POST   /narrative/keep-mine       keep the edit, set the newer summary aside
    - PUT    /overrides                 { targetType, targetId, field, value, note }
    - DELETE /overrides                 { targetType, targetId, field }
    - GET    /documents?documentRef=    one scan report's PDF (path from the adapter)
    - GET    /export.pdf                the summary as a PDF
  Realtime: `scanSummaryUpdated` ({ submissionId, organizationId, status }) on the org channel.

  There is no VUE_APP_ switch: the API's own flag decides. `probe` reads the summary once
  per page load; a 200 turns the scan UI on, and any other answer (404 while the flag is
  off, a 500, no connection) leaves it off for that load, without a toast.

  Every write answers `{ success, message }`. The message is one fixed sentence; the
  server's own text never reaches the broker.
*/

export const SCAN_NOT_REACHABLE = NOT_REACHABLE;
export const SCAN_NOT_FOUND = "This scan summary is no longer available. Reload the page and try again.";
export const REGENERATE_FAILED = "We couldn't start updating the summary. Try again in a minute.";
export const NARRATIVE_SAVE_FAILED = "We couldn't save your summary. Try again.";
export const NARRATIVE_EMPTY = "Write a summary, or use the generated one.";
export const NARRATIVE_TOO_LONG = "This summary is too long. Shorten it and try again.";
export const REBUILD_RUNNING = "A rebuild is already running. Try again in a minute.";
export const NARRATIVE_CHOICE_FAILED = "We couldn't update the summary. Try again.";
export const CORRECTION_SAVE_FAILED = "We couldn't save the correction. Try again.";
export const CORRECTION_CLEAR_FAILED = "We couldn't clear the correction. Try again.";
export const CORRECTION_EMPTY = "Enter a value for this correction.";

const PDF_TYPE = "application/pdf";
// A report PDF or the export can take a while to fetch or render.
const PDF_TIMEOUT_MS = 60000;

const scanUrl = (orgId, submissionId, suffix = "") =>
  `/brokers/customers/${orgId}/submissions/${submissionId}/scan-summary${suffix}`;

/**
 * One plain sentence for a refused write, chosen by what happened; never the server's
 * text. `invalid` is what a 422 means for this write, when it means something.
 */
const failure = (e, fallback, invalid) => {
  if (e && !e.response) return SCAN_NOT_REACHABLE;
  const status = e && e.response && e.response.status;
  if (status === 404) return SCAN_NOT_FOUND;
  if (status === 409) return REBUILD_RUNNING;
  if (status === 422 && invalid) return invalid;
  return fallback;
};

const QUIET = { withoutLoading: true, withoutErrorHandler: true };

/** A PDF Blob from an axios blob response, or null when the body is anything else. */
export function pdfBlobFrom(response) {
  const blob = response && response.data;
  if (typeof Blob === "undefined" || !(blob instanceof Blob) || !blob.size) return null;
  const header = (response.headers && response.headers["content-type"]) || "";
  const type = String(blob.type || header)
    .split(";")[0]
    .trim()
    .toLowerCase();
  if (type !== PDF_TYPE) return null;
  return blob.type === PDF_TYPE ? blob : new Blob([blob], { type: PDF_TYPE });
}

/*
  Per-submission request order. Every read and write takes the next number; an answer is
  kept only while its number is still the latest, so a slow older read can never put an
  older summary over a newer one.
*/
const requestSeq = {};
const nextSeq = (submissionId) => {
  requestSeq[submissionId] = (requestSeq[submissionId] || 0) + 1;
  return requestSeq[submissionId];
};
const isLatest = (submissionId, seq) => requestSeq[submissionId] === seq;

// One probe in flight per org and submission: a route change that asks twice shares it.
const probesInFlight = {};

const isNotFound = (e) => Boolean(e && e.response && e.response.status === 404);

export default {
  namespaced: true,

  state: () => ({
    // submissionId -> true when the API answered the probe with a summary.
    enabled: {},
    // submissionId -> the summary payload.
    summaryCache: {},
  }),

  mutations: {
    setEnabled(state, { submissionId, enabled }) {
      if (!submissionId) return;
      Vue.set(state.enabled, submissionId, Boolean(enabled));
    },
    cacheSummary(state, { submissionId, summary }) {
      if (!submissionId || !summary || typeof summary !== "object") return;
      Vue.set(state.summaryCache, submissionId, adaptScanSummary(summary));
    },
    /** A scan report was just uploaded: the summary is out of date until it is rebuilt. */
    markUpdating(state, { submissionId }) {
      const existing = state.summaryCache[submissionId];
      if (!existing) return;
      Vue.set(state.summaryCache, submissionId, { ...existing, status: "stale" });
    },
  },

  actions: {
    /**
     * Read the summary to learn whether the scan UI is on. Never toasts. A 404 turns it
     * off; any other failure leaves it off only when it was never on, so a passing error
     * does not hide a view that works.
     */
    probe({ commit, state }, { orgId, submissionId } = {}) {
      if (!orgId || !submissionId) return Promise.resolve(false);
      const key = `${orgId}/${submissionId}`;
      if (probesInFlight[key]) return probesInFlight[key];
      const seq = nextSeq(submissionId);
      const run = (async () => {
        try {
          const response = await axios.get(scanUrl(orgId, submissionId), QUIET);
          const data = response && response.data;
          const ok = Boolean(response && response.status === 200 && data && data.success && data.summary);
          if (ok) {
            commit("setEnabled", { submissionId, enabled: true });
            if (isLatest(submissionId, seq)) commit("cacheSummary", { submissionId, summary: data.summary });
          } else if (state.enabled[submissionId] !== true) {
            commit("setEnabled", { submissionId, enabled: false });
          }
          return state.enabled[submissionId] === true;
        } catch (e) {
          if (isNotFound(e) || state.enabled[submissionId] !== true) {
            commit("setEnabled", { submissionId, enabled: false });
          }
          return state.enabled[submissionId] === true;
        } finally {
          delete probesInFlight[key];
        }
      })();
      probesInFlight[key] = run;
      return run;
    },

    async fetchSummary({ commit, state }, { orgId, submissionId, refresh = false } = {}) {
      if (!orgId || !submissionId) return null;
      if (!refresh && state.summaryCache[submissionId]) return state.summaryCache[submissionId];
      const seq = nextSeq(submissionId);
      try {
        const suffix = refresh ? `?_=${Date.now()}` : "";
        const { data } = await axios.get(scanUrl(orgId, submissionId, suffix), QUIET);
        if (!data || !data.success || !data.summary) return null;
        // A newer read or write has started since: its answer is the one to keep.
        if (!isLatest(submissionId, seq)) return state.summaryCache[submissionId] || adaptScanSummary(data.summary);
        commit("cacheSummary", { submissionId, summary: data.summary });
        return state.summaryCache[submissionId];
      } catch (e) {
        if (isNotFound(e)) commit("setEnabled", { submissionId, enabled: false });
        return null;
      }
    },

    async regenerate(context, { orgId, submissionId } = {}) {
      return write(context, {
        orgId,
        submissionId,
        method: "post",
        suffix: "/regenerate",
        fallback: REGENERATE_FAILED,
      });
    },

    async saveNarrative(context, { orgId, submissionId, text } = {}) {
      const clean = String(text || "").trim();
      if (!clean) return { success: false, message: NARRATIVE_EMPTY };
      if (clean.length > NARRATIVE_MAX) return { success: false, message: NARRATIVE_TOO_LONG };
      return write(context, {
        orgId,
        submissionId,
        method: "put",
        suffix: "/narrative",
        body: { text: clean },
        fallback: NARRATIVE_SAVE_FAILED,
        invalid: NARRATIVE_TOO_LONG,
      });
    },

    async acceptNewerNarrative(context, { orgId, submissionId } = {}) {
      return write(context, {
        orgId,
        submissionId,
        method: "post",
        suffix: "/narrative/accept-newer",
        fallback: NARRATIVE_CHOICE_FAILED,
      });
    },

    async keepMyNarrative(context, { orgId, submissionId } = {}) {
      return write(context, {
        orgId,
        submissionId,
        method: "post",
        suffix: "/narrative/keep-mine",
        fallback: NARRATIVE_CHOICE_FAILED,
      });
    },

    async saveOverride(context, { orgId, submissionId, targetType, targetId, field, value, note } = {}) {
      const clean = String(value == null ? "" : value).trim();
      if (!clean) return { success: false, message: CORRECTION_EMPTY };
      if (!targetType || !targetId || !field) return { success: false, message: CORRECTION_SAVE_FAILED };
      const trimmedNote = String(note || "").trim();
      return write(context, {
        orgId,
        submissionId,
        method: "put",
        suffix: "/overrides",
        body: { targetType, targetId, field, value: clean, note: trimmedNote || null },
        fallback: CORRECTION_SAVE_FAILED,
      });
    },

    async clearOverride(context, { orgId, submissionId, targetType, targetId, field } = {}) {
      if (!targetType || !targetId || !field) return { success: false, message: CORRECTION_CLEAR_FAILED };
      return write(context, {
        orgId,
        submissionId,
        method: "delete",
        suffix: "/overrides",
        body: { targetType, targetId, field },
        fallback: CORRECTION_CLEAR_FAILED,
      });
    },

    /** One scan report's PDF as a Blob, or null. Silent: the screen says what failed. */
    async fetchDocumentPdf(_context, { orgId, submissionId, documentId } = {}) {
      if (!orgId || !submissionId || !documentId) return null;
      try {
        const response = await axios.get(scanUrl(orgId, submissionId, scanDocumentPath(documentId)), {
          ...QUIET,
          responseType: "blob",
          timeout: PDF_TIMEOUT_MS,
        });
        return pdfBlobFrom(response);
      } catch (e) {
        return null;
      }
    },

    /** The exported summary as a PDF Blob, or null when the answer is not a PDF. */
    async fetchExportPdf(_context, { orgId, submissionId } = {}) {
      if (!orgId || !submissionId) return null;
      try {
        const response = await axios.get(scanUrl(orgId, submissionId, "/export.pdf"), {
          ...QUIET,
          responseType: "blob",
          timeout: PDF_TIMEOUT_MS,
        });
        return pdfBlobFrom(response);
      } catch (e) {
        return null;
      }
    },

    /** Pusher `scanSummaryUpdated`: re-read only a summary this tab already holds. */
    onPusherScanSummaryUpdated({ state, dispatch }, payload) {
      const submissionId = payload && (payload.submissionId || payload.submission_id);
      if (!submissionId) return;
      const cached = state.summaryCache[submissionId];
      if (!cached) return;
      const orgId = payload.organizationId || payload.organization_id || cached.organizationId || null;
      if (!orgId) return;
      dispatch("fetchSummary", { orgId, submissionId, refresh: true });
    },
  },

  getters: {
    enabledFor: (state) => (submissionId) => state.enabled[submissionId] === true,
    summaryFor: (state) => (submissionId) => state.summaryCache[submissionId] || null,
    /** True only once the API has answered the probe with "off"; unanswered is not off. */
    knownOffFor: (state) => (submissionId) => state.enabled[submissionId] === false,
  },
};

/**
 * One write: send it, then cache the summary the API answers with, or give one fixed
 * sentence. A successful write takes the next place in the request order, so a read that
 * started before it and answers after it is discarded. A 404 means the scan summary is off.
 */
async function write({ commit }, { orgId, submissionId, method, suffix, body, fallback, invalid }) {
  if (!orgId || !submissionId) return { success: false, message: fallback };
  try {
    const url = scanUrl(orgId, submissionId, suffix);
    let response;
    if (method === "delete") response = await axios.delete(url, { ...QUIET, data: body });
    else if (method === "put") response = await axios.put(url, body, QUIET);
    else response = await axios.post(url, body || {}, QUIET);
    const data = response && response.data;
    if (!data || !data.success || !data.summary) return { success: false, message: fallback };
    nextSeq(submissionId);
    commit("cacheSummary", { submissionId, summary: data.summary });
    return { success: true, summary: adaptScanSummary(data.summary) };
  } catch (e) {
    if (isNotFound(e)) commit("setEnabled", { submissionId, enabled: false });
    return { success: false, message: failure(e, fallback, invalid) };
  }
}
